import { useEffect } from 'react';

import { isApplicationModeInfinity } from 'src/data/system';
import { VISUAL_LANGUAGE_ASSET_FIELD } from 'src/data/visualLanguage';
import { isAbortError } from 'src/lib/errors';
import { createStateSelectorHook } from 'src/lib/sweetState';
import { useImageStyleActions } from 'src/store/imageStyle';
import { useVisualLanguageActions, VisualLanguageStore } from 'src/store/visualLanguage';
import { useCurrentWorkspaceId } from 'src/store/workspace';

const useVisualLanguageState = createStateSelectorHook(VisualLanguageStore, [
  'associatedAssets',
  'associatedAssetsLoaded',
]);

/**
 * This hook is maintaining the "visual language" store state + relevant assets stores
 */
export const useMaintainVisualLanguageStoreState = () => {
  const currentWorkspaceId = useCurrentWorkspaceId();
  const { associatedAssets, associatedAssetsLoaded } = useVisualLanguageState();
  const visualLanguageActions = useVisualLanguageActions();
  const imageStyleActions = useImageStyleActions();

  /**
   * Fetches visual languages (and their assets) for the current workspace
   */
  useEffect(() => {
    if (!currentWorkspaceId) {
      return;
    }

    // we don't need "visual languages" in Infinity
    if (isApplicationModeInfinity()) {
      return;
    }

    // Create an abort controller, in case user selects another workspace before the fetch completes
    const abortController = new AbortController();

    // Fetch brand voices for the current workspace
    try {
      visualLanguageActions.queryWorkspaceVisualLanguagesWithAssets(
        { workspaceId: currentWorkspaceId },
        {
          abortSignal: abortController.signal,
        }
      );
    } catch (error) {
      if (isAbortError(error)) {
        // If the error is an abort error, we can ignore it
        return;
      }
    }

    return () => {
      abortController.abort();
    };
  }, [currentWorkspaceId, visualLanguageActions]);

  /**
   * When the associated assets are loaded, we merge them into the relevant stores
   */
  useEffect(() => {
    if (!associatedAssetsLoaded) {
      return;
    }

    imageStyleActions.initWorkspaceStyles(
      associatedAssets[VISUAL_LANGUAGE_ASSET_FIELD.imageStyles] || []
    );
  }, [associatedAssetsLoaded, associatedAssets, imageStyleActions]);
};
